import React, { useEffect, useState } from 'react';
import * as Bluebird from 'bluebird';
import PropTypes from 'prop-types';
import { metersToFeet, splitLastChar } from '../../../lib/utils';
import { connect } from 'dva';
import styles from './Coordinates.module.scss';
import { COORD_TYPES } from '../../../lib/constants';
import ghAnalytics from '../../../lib/analytics';
import * as coordinateFormatter from '@arcgis/core/geometry/coordinateFormatter';
import Point from '@arcgis/core/geometry/Point';
import ElevationLayer from '@arcgis/core/layers/ElevationLayer';
import Extent from '@arcgis/core/geometry/Extent';

function CurrentCoords(props) {
  const [pointerMoveEvent, setPointerMoveEvent] = useState(null);
  const [interactingEvent, setInteractingEvent] = useState(null);
  const [mouseCoords, setMouseCoords] = useState(null);
  const [sampler, setSampler] = useState(null);
  const [loading, setLoading] = useState(true);

  let shouldUpdateLocation = true;

  const formatCoords = (coordsType, coordsStr) => {
    const coords = coordsStr.split(' ');

    if (coordsType === COORD_TYPES.dd) {
      const coordOneArray = splitLastChar(coords[0]);
      const coordTwoArray = splitLastChar(coords[1]);

      coordsStr =
        coordOneArray[0] +
        '°' +
        coordOneArray[1] +
        ', ' +
        coordTwoArray[0] +
        '°' +
        coordTwoArray[1];
    } else if (coordsType === COORD_TYPES.ddm) {
      const coordOne = coords[0];
      const coordTwoArray = splitLastChar(coords[1]);
      const coordThree = coords[2];
      const coordFourArray = splitLastChar(coords[3]);

      coordsStr =
        coordOne +
        '° ' +
        coordTwoArray[0] +
        '\'' +
        coordTwoArray[1] +
        ', ' +
        coordThree +
        '° ' +
        coordFourArray[0] +
        '\'' +
        coordFourArray[1];
    } else if (coordsType === COORD_TYPES.dms) {
      const coordOne = coords[0];
      const coordTwo = coords[1];
      const coordThreeArray = splitLastChar(coords[2]);
      const coordFour = coords[3];
      const coordFive = coords[4];
      const coordSixArray = splitLastChar(coords[5]);

      coordsStr =
        coordOne +
        '° ' +
        coordTwo +
        '\' ' +
        coordThreeArray[0] +
        '"' +
        coordThreeArray[1] +
        ', ' +
        coordFour +
        '° ' +
        coordFive +
        '\' ' +
        coordSixArray[0] +
        '"' +
        coordSixArray[1];
    }

    return coordsStr;
  };

  const processCoords = (e) => {
    try {
      const pt = props.view.toMap(e);
      const result = sampler.queryElevation(pt);

      let coordsStr = '';
      const { coordsType } = props.mapParams;

      if (coordsType === COORD_TYPES.basemap) {
        // coordsStr = `${result.x.toFixed(3)}, ${result.y.toFixed(3)}, ${parseInt(
        //   metersToFeet(result.z)
        // )} ft`;
        coordsStr = `${result.x.toFixed(3)}, ${result.y.toFixed(3)}`;
        return coordsStr;
        // setCoordsDisplayStr(coordsStr);
      } else if (coordsType === COORD_TYPES.iso6709) {
        /* 
          - Commenting out coordString with elevation data until accurate reading is resolved
          coordsStr = `${result.latitude.toFixed(6)}°, ${result.longitude.toFixed(
            6
          )}°, ${parseInt(metersToFeet(result.z))} ft`;
        */
        coordsStr = `${result.latitude.toFixed(6)}°, ${result.longitude.toFixed(
          6
        )}°`;
        return coordsStr;
        // setCoordsDisplayStr(coordsStr);
      } else if (coordsType === COORD_TYPES.xy) {
        coordsStr = `${result.longitude.toFixed(6)}°, ${result.latitude.toFixed(
          6
        )}°`;
        return coordsStr;
        // setCoordsDisplayStr(coordsStr);
      } else {
        // coordinateFormatter.load().then(() => {
        if (
          coordsType === COORD_TYPES.dd ||
          coordsType === COORD_TYPES.ddm ||
          coordsType === COORD_TYPES.dms
        ) {
          coordsStr = coordinateFormatter.toLatitudeLongitude(
            new Point({
              longitude: result.longitude,
              latitude: result.latitude,
            }),
            coordsType.toLowerCase(),
            6
          );
          coordsStr = formatCoords(coordsType, coordsStr);
        } else if (coordsType === COORD_TYPES.mgrs) {
          coordsStr = coordinateFormatter.toMgrs(
            new Point({
              longitude: result.longitude,
              latitude: result.latitude,
            }),
            'automatic',
            5,
            true
          );
        } else if (coordsType === COORD_TYPES.usng) {
          coordsStr = coordinateFormatter.toUsng(
            new Point({
              longitude: result.longitude,
              latitude: result.latitude,
            }),
            5,
            true
          );
        } else if (coordsType === COORD_TYPES.utm) {
          coordsStr = coordinateFormatter.toUtm(
            new Point({
              longitude: result.longitude,
              latitude: result.latitude,
            }),
            'north-south-indicators',
            true
          );
        }

        return coordsStr;
      }
    } catch (e) {
      return '--';
    }
  };

  useEffect(() => {
    if (props.view.ui.find('coords') != null) {
      return;
    }
    props.dispatch({
      type: 'map/setMapLoading',
      payload: true
    })
    setLoading(true);

    props.view
      .when(() => {
        const elevation = new ElevationLayer({
          //default elevation service from ESRI
          url: 'http://elevation3d.arcgis.com/arcgis/rest/services/WorldElevation3D/Terrain3D/ImageServer',
        });
        return Bluebird.all([elevation.load(), coordinateFormatter.load()]);
      })
      .then(([elevationSampler, _]) => {
        // console.log('FORMATTER', coordFormatter);
        /******this method will take an extent of the map and query the elevation service.
          The extent is the intially loaded extent. Make sure your extent loaded is what you
          want the user to query x,y,z values.
          Allow us to perform client side queries for elevation, making it super fast.*******/

        /*
            #NOTE: Because of screen size/resolution, some users will see map that includes Canada and
            South America as well upon initialization, and these users might see errors due to too many
            requests being made to elevation service. This is because we use props.view.extent.

            After talking with Justin M, and Stewart, we've decided to hardcode extent that best covers
            US (excluding Alaska). This way, for all users, it loads same amount of data, and calls
            should no longer fail.
            -- KD - November 04, 2020
          */

        // setFormatter(coordFormatter);

        const extent = new Extent({
          xmin: -125.283291,
          xmax: -60.896542, // -76.667181,
          ymax: 49.154397,
          ymin: 27.048534, // 30.988365,
        });

        elevationSampler
          .createElevationSampler(extent, {
            // demResolution: 500,
            demResolution: 'auto',
          })
          .then((sampler) => {
            setSampler(sampler);
            props.dispatch({
              type: 'map/setMapLoading',
              payload: false
            })
            setLoading(false);
            const interactingEvent = props.view.watch('interacting', (e) => {
              shouldUpdateLocation = !e;
            });

            /*
              #NOTE: Keep these events and its related code for reference, for now.
              -- KD - November 05, 2020
            */
            // const mouseWheelEvent = props.view.on('mouse-wheel',(e) => {
            // });

            // props.view.watch('zoom', function(zoomLevel) {
            // });

            // props.view.watch('stationary',(e) => {
            // });

            // const mapDragEvent = props.view.on('drag',(e) => {
            //   shouldUpdateLocation = false;
            //   if ( e.action === 'end' ) {
            //     shouldUpdateLocation = true;
            //   }
            // });

            const pointerMoveEvent = props.view.on('pointer-move', (e) => {
              if (shouldUpdateLocation) {
                setMouseCoords(e);
              }
            });

            setInteractingEvent(interactingEvent);
            setPointerMoveEvent(pointerMoveEvent);
          });
      });
    // })
    // .catch((err) => console.log(err));

    return () => {
      if (interactingEvent) {
        interactingEvent.remove();
      }
      if (pointerMoveEvent) {
        pointerMoveEvent.remove(); // Remove the .on() listener on view.
      }
    };
  }, [props.view]);

  const changeFormat = (e) => {
    const coordsType = e.target.value;

    props.dispatch({
      type: 'map/setMapParams',
      payload: {
        coordsType,
      },
    });

    ghAnalytics.logCoordinateSystemUpdated(coordsType);
  };

  return (
    <div
      className={`esri-component esri-coordinate-conversion esri-widget ${styles.container}`}
    >
      <div className={styles.conversionsView}>
        <ul
          className='esri-coordinate-conversion__conversion-list'
          id='coords__esri-coordinate-conversion__conversion-list'
        >
          <li className='esri-coordinate-conversion__row' role='group'>
            <select
              onChange={changeFormat}
              value={props.mapParams.coordsType}
              className='esri-select esri-coordinate-conversion__select-row'
              title='Select format'
            >
              {Object.values(COORD_TYPES).map((coord) => {
                return (
                  <option key={coord} value={coord}>
                    {coord}
                  </option>
                );
              })}
            </select>
            <div
              className='esri-coordinate-conversion__display'
              role='listitem'
            >
              {loading
                ? 'Loading...'
                : (props.hideCoords
                    ? '--'
                    : processCoords(mouseCoords)
                  )
              }
            </div>
            {/* <ul className="esri-coordinate-conversion__tools">
              <li aria-controls="coords__esri-coordinate-conversion__conversion-list" aria-label="Expand" className="esri-widget--button" role="button" tabIndex="0" title="Expand">
                <span aria-hidden="true" className="esri-icon-down"></span>
              </li>
            </ul> */}
          </li>
        </ul>
      </div>
    </div>
  );
}

CurrentCoords.propTypes = {
  mapMode: PropTypes.string,
  view: PropTypes.object,
  mapParams: PropTypes.object,
  hideCoords: PropTypes.bool,
};

export default connect((model) => {
  return {
    mapMode: model.map.mapMode,
    mapParams: model.map.mapParams,
    hideCoords: model.app.hideCoords,
  };
})(CurrentCoords);
